import { useState } from 'react';
import { Loader2, Search, Play, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function EntityRecordsSyncPanel() {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [decisions, setDecisions] = useState({});

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setApplyResult(null);
    setDecisions({});
    try {
      const { data } = await diagnosticsAPI.entitySyncPreview();
      setPreview(data);
      // Default all diverged to "update"
      const defaultDecisions = {};
      (data.diverged || []).forEach((d) => {
        if (d.uuid) defaultDecisions[d.uuid] = 'update';
      });
      setDecisions(defaultDecisions);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await diagnosticsAPI.entitySyncApply({
        create_missing: true,
        fix_divergences: preview?.diverged_count > 0,
        entity_decisions: decisions,
      });
      setApplyResult(data);
      setPreview(null);
      toast.success(`Created ${data.created_count}, updated ${data.updated_count}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const toggleDecision = (uuid) => {
    setDecisions((prev) => ({
      ...prev,
      [uuid]: prev[uuid] === 'update' ? 'skip' : 'update',
    }));
  };

  const needsAction = preview && (preview.missing_count > 0 || preview.diverged_count > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Entity Records Sync</CardTitle>
        <p className="text-xs text-muted-foreground">
          Compare ENTITY_DEFS (source of truth) with db.entities. Create missing records
          and reconcile diverged fields with per-entity decisions.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={runPreview} disabled={loading || applying} variant="outline" size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          {needsAction && (
            <Button onClick={runApply} disabled={applying} size="sm">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              Apply
            </Button>
          )}
        </div>

        {preview && (
          <div className="space-y-4">
            {/* Summary metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Registry</p>
                <p className="text-xl font-bold tabular-nums">{preview.total_in_registry}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">In DB</p>
                <p className="text-xl font-bold tabular-nums">{preview.total_in_db}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Missing</p>
                <p className={`text-xl font-bold tabular-nums ${preview.missing_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {preview.missing_count}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Diverged</p>
                <p className={`text-xl font-bold tabular-nums ${preview.diverged_count > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {preview.diverged_count}
                </p>
              </div>
            </div>

            {/* In-sync message */}
            {!needsAction && preview.orphaned_count === 0 && (
              <div className="rounded-lg border p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                <p className="text-sm text-muted-foreground">
                  All {preview.ok_count} entities are in sync.
                </p>
              </div>
            )}

            {/* Missing entities */}
            {preview.missing?.length > 0 && (
              <details open className="border rounded-lg">
                <summary className="px-3 py-2 cursor-pointer text-sm font-medium flex items-center gap-2 hover:bg-muted/30">
                  <Badge variant="destructive" className="text-xs">{preview.missing_count} missing</Badge>
                  Will be created on Apply
                </summary>
                <div className="px-3 pb-3">
                  <div className="overflow-x-auto border rounded mt-2">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1.5 font-medium">Entity ID</th>
                          <th className="text-left px-2 py-1.5 font-medium">Name</th>
                          <th className="text-left px-2 py-1.5 font-medium">Country</th>
                          <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                          <th className="text-left px-2 py-1.5 font-medium">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.missing.map((e) => (
                          <tr key={e.entity_id} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">{e.entity_id}</td>
                            <td className="px-2 py-1">{e.canonical_name}</td>
                            <td className="px-2 py-1">{e.country}</td>
                            <td className="px-2 py-1">{e.currency}</td>
                            <td className="px-2 py-1">{e.group_role}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            )}

            {/* Diverged entities */}
            {preview.diverged?.length > 0 && (
              <details open className="border rounded-lg">
                <summary className="px-3 py-2 cursor-pointer text-sm font-medium flex items-center gap-2 hover:bg-muted/30">
                  <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                    {preview.diverged_count} diverged
                  </Badge>
                  Toggle per-entity decision below
                </summary>
                <div className="px-3 pb-3 space-y-2 mt-2">
                  {preview.diverged.map((d) => (
                    <div key={d.uuid} className="border rounded p-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          <span className="font-mono text-xs">{d.entity_id}</span>{' '}
                          {d.canonical_name}
                        </span>
                        <Button
                          variant={decisions[d.uuid] === 'update' ? 'default' : 'outline'}
                          size="sm"
                          className="text-xs h-6"
                          onClick={() => toggleDecision(d.uuid)}
                        >
                          {decisions[d.uuid] === 'update' ? 'Update' : 'Skip'}
                        </Button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="text-left px-2 py-1 font-medium">Field</th>
                              <th className="text-left px-2 py-1 font-medium">Stored</th>
                              <th className="text-left px-2 py-1 font-medium">Expected</th>
                            </tr>
                          </thead>
                          <tbody>
                            {d.diffs.map((diff, i) => (
                              <tr key={i} className="border-b last:border-0">
                                <td className="px-2 py-1 font-mono">{diff.field}</td>
                                <td className="px-2 py-1 text-red-600">{String(diff.stored ?? 'null')}</td>
                                <td className="px-2 py-1 text-green-600">{String(diff.expected ?? 'null')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Orphaned entities */}
            {preview.orphaned?.length > 0 && (
              <details className="border rounded-lg">
                <summary className="px-3 py-2 cursor-pointer text-sm font-medium flex items-center gap-2 hover:bg-muted/30">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  {preview.orphaned_count} orphaned (in DB but not in ENTITY_DEFS)
                </summary>
                <div className="px-3 pb-3">
                  <p className="text-xs text-muted-foreground mb-2">
                    These records exist in db.entities but have no matching canonical_name in ENTITY_DEFS.
                    They are never auto-deleted.
                  </p>
                  <div className="overflow-x-auto border rounded mt-2">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1.5 font-medium">UUID</th>
                          <th className="text-left px-2 py-1.5 font-medium">Name</th>
                          <th className="text-left px-2 py-1.5 font-medium">Type</th>
                          <th className="text-left px-2 py-1.5 font-medium">Country</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.orphaned.map((o, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono truncate max-w-[150px]">{o.uuid}</td>
                            <td className="px-2 py-1">{o.name}</td>
                            <td className="px-2 py-1 font-mono">{o.type}</td>
                            <td className="px-2 py-1">{o.country}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            )}
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className="rounded-lg border border-green-300 dark:border-green-800 p-3 bg-green-50 dark:bg-green-950/30 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">Sync applied</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-sm font-medium tabular-nums">{applyResult.created_count}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Updated</p>
                <p className="text-sm font-medium tabular-nums">{applyResult.updated_count}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Skipped</p>
                <p className="text-sm font-medium tabular-nums">{applyResult.skipped_count}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
